'use client'
// Global, MetaMarket-independent and lazy. Existing market stays unmounted.
// Existing notification and lifecycle signals request server verification; they cannot grant.
// Event-driven with one bounded post-award continuation; never an interval or idle poll.
import React, { useCallback, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { buildQcoinClientAuthHeaders } from '../lib/qcoinClientRuntime.js'
import ceremonyPolicy from '../lib/achievements/ceremony-scheduler.cjs'

const Overlay = dynamic(() => import('./metamarket/AchievementCelebrationOverlay.jsx'), { ssr: false })
const ENABLED = process.env.NEXT_PUBLIC_ACHIEVEMENTS_ENABLED === 'true'
const MIN_SOURCE_REFRESH_MS = 120_000
const {
  MIN_SIGNAL_GAP_MS, MAX_CEREMONIES_PER_SESSION, eligibleCeremonies,
  hasVerifyingAwards, boundedEvaluationRetryDelay, shouldProbe,
} = ceremonyPolicy

function viewerHint() {
  // Only used to avoid polling fully anonymous clients, NOT server authority.
  try {
    const w = window
    return String(w.__AUTH_ACCOUNT__ || w.__ASHER_ID__ || w.__QL7_UID__ ||
      w.__FORUM_USER__ || w.localStorage?.getItem('account') ||
      w.localStorage?.getItem('wallet') || w.localStorage?.getItem('ql7_uid') || '').trim()
  } catch { return '' }
}
function actorScope(auth) {
  const session = auth.session || {}
  // In-memory signal to reset queues on switching wallet/login; no trust in
  // this hint for deciding ownership. The API resolves the canonical actor.
  // Browser-only scope fingerprint; raw Telegram initData is never retained
  // in the ceremony state or emitted in events/logs. Not an auth decision.
  const tmaData = String(auth.headers?.['x-telegram-init-data'] || '')
  let tmaHash = 2166136261
  for (let i = 0; i < tmaData.length; i++) tmaHash = Math.imul(tmaHash ^ tmaData.charCodeAt(i), 16777619) >>> 0
  const tma = tmaData ? ':tma-' + tmaHash.toString(16) : ''
  return String(session.token || '') + ':' + String(session.accountId || '') + ':' +
    String(session.walletAddress || '') + ':' + viewerHint() + tma
}

export default function AchievementCelebrationHost() {
  const [activeId, setActiveId] = useState('')
  const queueRef = useRef([])
  const shownRef = useRef(new Set())
  const inFlightRef = useRef(false)
  const lastProbeAtRef = useRef(0)
  const lastRefreshAtRef = useRef(0)
  const abortRef = useRef(null)
  const activeRef = useRef('')
  const actorScopeRef = useRef('')
  const deniedScopeRef = useRef('')
  const deferredTimerRef = useRef(null)
  const deferredDueAtRef = useRef(0)
  const deferredPendingRef = useRef(false)
  const deferredAttemptsRef = useRef(0)
  const deferredLoadRef = useRef(null)
  activeRef.current = activeId

  const clearDeferredEvaluation = useCallback(() => {
    if (deferredTimerRef.current != null) window.clearTimeout(deferredTimerRef.current)
    deferredTimerRef.current = null
    deferredDueAtRef.current = 0
    deferredPendingRef.current = false
  }, [])

  const scheduleDeferredEvaluation = useCallback((serverDelay) => {
    const bounded = boundedEvaluationRetryDelay(serverDelay)
    if (bounded == null || deferredAttemptsRef.current >= MAX_CEREMONIES_PER_SESSION) return false
    const dueAt = Date.now() + bounded
    // Multiple mounted consumers may observe the same committed grant. Keep
    // only the earliest server-bounded continuation for this global host.
    if (deferredTimerRef.current != null && deferredDueAtRef.current <= dueAt) return true
    if (deferredTimerRef.current != null) window.clearTimeout(deferredTimerRef.current)
    deferredDueAtRef.current = dueAt
    deferredPendingRef.current = false
    deferredTimerRef.current = window.setTimeout(() => {
      deferredTimerRef.current = null
      if (document.visibilityState !== 'visible' || activeRef.current || inFlightRef.current) {
        deferredPendingRef.current = true
        return
      }
      deferredDueAtRef.current = 0
      deferredAttemptsRef.current += 1
      deferredLoadRef.current?.({ refresh: true })
    }, bounded)
    return true
  }, [])

  const resumeDeferredEvaluation = useCallback(() => {
    if (!deferredPendingRef.current || document.visibilityState !== 'visible' ||
        activeRef.current || inFlightRef.current ||
        deferredAttemptsRef.current >= MAX_CEREMONIES_PER_SESSION) return false
    deferredPendingRef.current = false
    deferredDueAtRef.current = 0
    deferredAttemptsRef.current += 1
    deferredLoadRef.current?.({ refresh: true })
    return true
  }, [])

  const resetIdentity = useCallback(() => {
    abortRef.current?.abort()
    clearDeferredEvaluation()
    abortRef.current = null
    inFlightRef.current = false
    queueRef.current = []
    shownRef.current = new Set()
    lastProbeAtRef.current = 0
    lastRefreshAtRef.current = 0
    deferredAttemptsRef.current = 0
    deniedScopeRef.current = ''
    activeRef.current = ''
    setActiveId('')
    actorScopeRef.current = ''
  }, [clearDeferredEvaluation])

  const presentVerifiedSnapshot = useCallback((snapshot) => {
    const excluded = new Set([...shownRef.current, ...queueRef.current])
    if (activeRef.current) excluded.add(activeRef.current)
    const remaining = Math.max(0,
      MAX_CEREMONIES_PER_SESSION - shownRef.current.size - queueRef.current.length)
    const candidates = eligibleCeremonies(snapshot, excluded, remaining)
    if (!candidates.length) return false
    queueRef.current.push(...candidates.map(row => row.id))
    if (!activeRef.current) {
      const next = queueRef.current.shift()
      if (next) {
        shownRef.current.add(next)
        activeRef.current = next
        setActiveId(next)
      }
    }
    return true
  }, [])

  const loadVerified = useCallback(async ({ refresh = false } = {}) => {
    if (!ENABLED) return
    const auth = buildQcoinClientAuthHeaders({ json: true })
    const scope = actorScope(auth)
    if (actorScopeRef.current && scope !== actorScopeRef.current) resetIdentity()
    actorScopeRef.current = scope
    const hasLogin = auth.hasAuthority || Boolean(viewerHint())
    if ((deniedScopeRef.current && deniedScopeRef.current === scope) || !shouldProbe({
      visible: document.visibilityState === 'visible', authenticated: hasLogin,
      active: Boolean(activeRef.current), inFlight: inFlightRef.current,
      now: Date.now(), lastAt: lastProbeAtRef.current,
      minGapMs: MIN_SIGNAL_GAP_MS,
    }) || shownRef.current.size >= MAX_CEREMONIES_PER_SESSION) return
    const currentTime = Date.now()
    lastProbeAtRef.current = currentTime
    // A wake-up may check settled grants immediately, but cannot repeatedly
    // scan canonical sources before the server's bounded refresh TTL.
    const doRefresh = refresh && (!lastRefreshAtRef.current || currentTime - lastRefreshAtRef.current >= MIN_SOURCE_REFRESH_MS)
    if (doRefresh) lastRefreshAtRef.current = currentTime
    inFlightRef.current = true
    const controller = new AbortController()
    abortRef.current = controller
    try {
      const options = { headers: auth.headers, credentials: 'same-origin',
        cache: 'no-store', signal: controller.signal }
      const response = doRefresh
        ? await fetch('/api/achievements/refresh', { ...options, method: 'POST' })
        : await fetch('/api/achievements/summary', options)
      const evaluationEligible = doRefresh &&
        response.headers.get('x-ql7-achievement-evaluation') === 'eligible'
      if (response.status === 401 || response.status === 403) {
        deniedScopeRef.current = scope // Don't retry this identity every interval.
        return
      }
      if (!response.ok || controller.signal.aborted || actorScope(buildQcoinClientAuthHeaders({ json: true })) !== scope) return
      const snapshot = await response.json()
      if (controller.signal.aborted || actorScope(buildQcoinClientAuthHeaders({ json: true })) !== scope) return
      presentVerifiedSnapshot(snapshot)
      // Keep canonical source scans and QCoin work outside the render-critical
      // snapshot request. The server chooses the award and owns a slot lease;
      // this wake-up contains no metrics or award ID.
      if (evaluationEligible) {
        try {
          const evaluation = await fetch('/api/achievements/evaluate', {
            method: 'POST', headers: auth.headers, credentials: 'same-origin',
            cache: 'no-store', keepalive: true, signal: controller.signal,
          })
          if (!evaluation.ok || controller.signal.aborted ||
              actorScope(buildQcoinClientAuthHeaders({ json: true })) !== scope) return
          const result = await evaluation.json().catch(() => null)
          let continuationSnapshot = snapshot
          if (result?.awardCommitted === true) {
            // Do not loop through the ordinary wake-up event: it intentionally
            // enforces an 8s anti-spam gap and could discard this same-request
            // commit. Read the ledger-backed projection directly in this
            // authenticated chain and present it without polling or a timer.
            const committed = await fetch('/api/achievements/summary', options)
            if (committed.ok && !controller.signal.aborted &&
                actorScope(buildQcoinClientAuthHeaders({ json: true })) === scope) {
              const committedSnapshot = await committed.json()
              if (!controller.signal.aborted &&
                  actorScope(buildQcoinClientAuthHeaders({ json: true })) === scope) {
                continuationSnapshot = committedSnapshot
                presentVerifiedSnapshot(committedSnapshot)
              }
            }
            // Compatibility wake-up for other mounted consumers. This host no
            // longer relies on it to show the ceremony.
            window.dispatchEvent(new Event('ql7:achievement-award-committed'))
          }
          if ((result?.status === 'awarded' || result?.status === 'already_scheduled') &&
              hasVerifyingAwards(continuationSnapshot)) {
            // Wait for both the server slot boundary and the bounded source
            // refresh TTL. This is one continuation for a known pending grant,
            // not a recurring achievement poll.
            const sourceReadyIn = lastRefreshAtRef.current
              ? Math.max(0, lastRefreshAtRef.current + MIN_SOURCE_REFRESH_MS + 250 - Date.now())
              : 0
            scheduleDeferredEvaluation(Math.max(Number(result?.retryAfterMs) || 0, sourceReadyIn))
          }
        } catch {
          // Notification/focus recovery still reads the committed server state.
        }
      }
    } catch {
      // Never fall back to browser counters or an optimistic award.
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
        inFlightRef.current = false
      }
    }
  }, [presentVerifiedSnapshot, resetIdentity, scheduleDeferredEvaluation])

  deferredLoadRef.current = loadVerified

  useEffect(() => {
    if (!ENABLED) return undefined
    const onCount = event => {
      const detail = event?.detail || {}
      if (detail.source === 'metamarket_gifts' && Number(detail.count) > 0) void loadVerified()
    }
    const onState = event => {
      if (Number(event?.detail?.counts?.metamarket_gifts || 0) > 0) void loadVerified()
    }
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        abortRef.current?.abort()
        abortRef.current = null
        inFlightRef.current = false
      } else {
        if (!resumeDeferredEvaluation()) void loadVerified({ refresh: true })
      }
    }
    const onLogin = () => {
      resetIdentity()
      // Read only on an actual login transition; no delayed background probe.
      void loadVerified({ refresh: true })
    }
    const onActivityCommitted = () => void loadVerified({ refresh: true })
    const onFocus = () => {
      if (document.visibilityState === 'visible' && !resumeDeferredEvaluation()) void loadVerified({ refresh: true })
    }
    window.addEventListener('ql7:notification-count', onCount)
    window.addEventListener('ql7:notification-state', onState)
    // Signal is an untrusted wakeup only; all grants are reverified server-side.
    window.addEventListener('ql7:achievement-activity-committed', onActivityCommitted)
    window.addEventListener('ql7:achievement-award-committed', onActivityCommitted)
    window.addEventListener('auth:ok', onLogin)
    window.addEventListener('auth:logout', onLogin)
    window.addEventListener('storage', onLogin)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    onState({ detail: window.__QL7_NOTIFICATION_STATE__ })
    // One snapshot at first mount can recover an unseen committed grant after
    // offline delivery. Subsequent reads are triggered by existing events.
    void loadVerified()
    return () => {
      window.removeEventListener('ql7:notification-count', onCount)
      window.removeEventListener('ql7:notification-state', onState)
      window.removeEventListener('ql7:achievement-activity-committed', onActivityCommitted)
      window.removeEventListener('ql7:achievement-award-committed', onActivityCommitted)
      window.removeEventListener('auth:ok', onLogin)
      window.removeEventListener('auth:logout', onLogin)
      window.removeEventListener('storage', onLogin)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
      abortRef.current?.abort()
      clearDeferredEvaluation()
      queueRef.current = []
    }
  }, [clearDeferredEvaluation, loadVerified, resetIdentity, resumeDeferredEvaluation])

  const close = useCallback(() => {
    const id = activeRef.current
    if (!id) return
    activeRef.current = ''
    setActiveId('')
    // Separate from firstViewedAt/New; server validates receipt/ledger again.
    const headers = buildQcoinClientAuthHeaders({ json: true }).headers
    void fetch('/api/achievements/mark-celebrated', {
      method: 'POST', credentials: 'same-origin', cache: 'no-store',
      headers, body: JSON.stringify({ achievementId: id }),
    }).catch(() => {})
    const next = queueRef.current.shift()
    if (next) {
      shownRef.current.add(next)
      activeRef.current = next
      setActiveId(next)
    } else {
      resumeDeferredEvaluation()
    }
  }, [resumeDeferredEvaluation])

  if (!ENABLED || !activeId) return null
  return <Overlay achievementId={activeId} onClose={close} />
}
